import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppShell from '../../../components/layout/AppShell';
import './quote-generator.css';
import { apiRequest, downloadApiFile } from '../../../api/client';
import { exportElementToPdf } from '../../../utils/exportPdf';
import { resolveBodySections, type PdfBodySection } from '../../../pdf/pdfCustomLayout';
import { resolveStudioLayout, type PdfStudioBranding } from '../../../pdf/pdfStudioState';

type CustomSectionValues = Record<string, { title: string; text: string }>;

function buildCustomSectionDefaults(
  sections: PdfBodySection[],
  saved?: Record<string, { title?: string; text?: string }> | null
): CustomSectionValues {
  const next: CustomSectionValues = {};
  for (const section of sections) {
    const savedRow = saved?.[section.id];
    next[section.id] = {
      title: (savedRow?.title ?? section.title ?? 'Section').trim() || 'Section',
      text: savedRow?.text ?? section.text ?? ''
    };
  }
  return next;
}

interface LineItem {
    id: string;
    item: string;
    description: string;
    quantity: number;
    unitPrice: number;
}

/** DB-loaded line items often lack `id`; without unique ids, editing one row updates every row. */
const ensureLineItemIds = (items: unknown): LineItem[] => {
    if (!Array.isArray(items)) return [];
    return items.map((raw, index) => {
        const row = (raw && typeof raw === 'object' ? raw : {}) as Partial<LineItem>;
        return {
            id: typeof row.id === 'string' && row.id ? row.id : `${Date.now()}-${index}`,
            item: row.item || '',
            description: row.description || '',
            quantity: Number(row.quantity) || 0,
            unitPrice: Number(row.unitPrice) || 0
        };
    });
};

interface ClientInfo {
    firstName: string;
    lastName: string;
    buildingName: string;
    address: string;
    phone: string;
    email: string;
    corpNo: string;
    representativeName: string;
}

interface QuoteOverview {
    jobTitle: string;
    salesperson: string;
    introduction: string;
}

interface ExistingClient {
    _id: string;
    firstName?: string;
    lastName?: string;
    buildingName?: string;
    address?: string;
    phone?: string;
    email?: string;
    corpNo?: string;
    representativeName?: string;
}

const displayNumberInput = (value: number): string | number => (value === 0 ? '' : value);
const isValidEmail = (value: string): boolean => /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(value.trim());
const isValidPhoneNumber = (value: string): boolean => {
    const digitsOnly = value.replace(/\D/g, '');
    return digitsOnly.length === 10 || (digitsOnly.length === 11 && digitsOnly.startsWith('1'));
};
const generateRandomQuoteNumber = (): number => Math.floor(1000 + Math.random() * 9000);
const COMPANY_NAME = 'Zera Fire Protection';
const COMPANY_ADDRESS = '27 Hemlock Crt, Barrie L4N9N5 Canada';
const COMPANY_PHONE = '+1 (437) 898-8866';
const COMPANY_EMAIL = 'info@zerafireprotection.com';
const COMPANY_WEBSITE = 'www.zerafireprotection.com';
const DEFAULT_ZERA_PDF_LOGO = '/assets/branding/zera-transparent.png';

export default function QuoteGenerator(): React.JSX.Element {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentQuoteId, setCurrentQuoteId] = useState<string | null>(null);
    const [currentQuoteSerial, setCurrentQuoteSerial] = useState<number | null>(generateRandomQuoteNumber());

    // 1. Client Information
    const [client, setClient] = useState<ClientInfo>({
        firstName: '',
        lastName: '',
        buildingName: '',
        address: '',
        phone: '',
        email: '',
        corpNo: '',
        representativeName: ''
    });

    // 2. Quote Overview
    const [overview, setOverview] = useState<QuoteOverview>({
        jobTitle: '',
        salesperson: 'Fermin Fierro', // Default from image
        introduction: ''
    });

    // 3. Line Items
    const [lineItems, setLineItems] = useState<LineItem[]>([
        { id: '1', item: 'Fire Alarm Inspection', description: 'Annual test and inspection', quantity: 1, unitPrice: 450.00 },
        { id: '2', item: 'Smoke Detector Replacement', description: 'Replace expired sensing unit', quantity: 2, unitPrice: 85.00 }
    ]);

    // 4. Pricing Summary
    const [discount, setDiscount] = useState<number>(0);
    const [taxRate, setTaxRate] = useState<number>(13);
    const [taxName, setTaxName] = useState<string>('HST');

    // Calculated fields
    const subtotal = lineItems.reduce((acc, item) => acc + (item.quantity * item.unitPrice), 0);
    const discountAmount = (subtotal * discount) / 100;
    const taxableAmount = subtotal - discountAmount;
    const taxAmount = (taxableAmount * taxRate) / 100;
    const total = taxableAmount + taxAmount;
    const deposit = total * 0.30; // 30% deposit

    // 5. Attachments/Misc
    const [attachments, setAttachments] = useState<string>('');
    const [imageDescription, setImageDescription] = useState<string>('');
    const [customerReview, setCustomerReview] = useState<string>('');
    const [clientMessage, setClientMessage] = useState<string>('Please review the attached quote. Feel free to contact us with any questions.');
    const [disclaimer] = useState<string>('This quote is valid for the next 30 days, after which values may be subject to change.');
    /** Custom body sections defined in PDF Studio for quotes. */
    const [studioCustomSections, setStudioCustomSections] = useState<PdfBodySection[]>([]);
    const [studioIntroTitle, setStudioIntroTitle] = useState('');
    const [studioSignatureEnabled, setStudioSignatureEnabled] = useState(false);
    const [studioSignatureTitle, setStudioSignatureTitle] = useState('Authorization');
    const [customSectionValues, setCustomSectionValues] = useState<CustomSectionValues>({});

    // Modal State
    const [showPreview, setShowPreview] = useState(false);
    const [confirmEmail, setConfirmEmail] = useState('');
    const [quoteStatus, setQuoteStatus] = useState<'draft' | 'sent' | string>('draft');
    const [isSending, setIsSending] = useState(false);
    const [isDownloading, setIsDownloading] = useState(false);
    const [isPreparingPdf, setIsPreparingPdf] = useState(false);
    const [showClientPicker, setShowClientPicker] = useState(false);
    const [clientSearch, setClientSearch] = useState('');
    const [existingClients, setExistingClients] = useState<ExistingClient[]>([]);
    const [isClientSearchLoading, setIsClientSearchLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [successMessage, setSuccessMessage] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    /** Zera-style PDF uses bundled logo unless tenant uploaded one in Settings. */
    const [zeraStylePdfLogoSrc, setZeraStylePdfLogoSrc] = useState(DEFAULT_ZERA_PDF_LOGO);

    useEffect(() => {
        apiRequest<{ branding?: PdfStudioBranding | null }>('/api/branding')
            .then((res) => {
                const branding = res.branding || {};
                const b64 = (branding.logoBase64 || '').trim();
                if (b64) setZeraStylePdfLogoSrc(b64);
                const layout = resolveStudioLayout(branding, 'quote');
                const bodySections = resolveBodySections(layout);
                const introSec = bodySections.find((s) => s.kind === 'intro' && s.enabled !== false);
                setStudioIntroTitle((introSec?.title || '').trim());
                const signatureSec = bodySections.find((s) => s.kind === 'signature');
                setStudioSignatureEnabled(Boolean(signatureSec && signatureSec.enabled !== false));
                setStudioSignatureTitle((signatureSec?.title || 'Authorization').trim() || 'Authorization');
                const customSecs = bodySections.filter(
                    (s) => s.kind === 'customText' && s.enabled !== false
                );
                setStudioCustomSections(customSecs);
                setCustomSectionValues((prev) => {
                    // Keep already-typed text when branding refreshes; prefer Studio titles for labels.
                    const seeded = buildCustomSectionDefaults(customSecs);
                    for (const sec of customSecs) {
                        if (prev[sec.id]?.text != null) {
                            seeded[sec.id] = {
                                title: (sec.title || prev[sec.id].title || 'Section').trim() || 'Section',
                                text: prev[sec.id].text
                            };
                        }
                    }
                    return seeded;
                });
            })
            .catch(() => {});
    }, []);

    // Load existing quote if editing
    useEffect(() => {
        const quoteId = searchParams.get('id');
        if (!quoteId) {
            setCurrentQuoteId(null);
            setCurrentQuoteSerial(generateRandomQuoteNumber());
            setQuoteStatus('draft');
            return;
        }

        apiRequest<{ data: any }>(`/api/quotes/${quoteId}`)
            .then((data) => {
                const existing = data.data;
                setCurrentQuoteId(existing._id);
                setCurrentQuoteSerial(typeof existing.serialNumber === 'number' ? existing.serialNumber : null);
                setQuoteStatus(existing.status || 'draft');

                const meta = (existing.meta && typeof existing.meta === 'object' ? existing.meta : {}) as Record<
                    string,
                    any
                >;
                const clientMeta = (meta.client && typeof meta.client === 'object' ? meta.client : {}) as Partial<ClientInfo>;
                const overviewMeta =
                    meta.overview && typeof meta.overview === 'object' ? meta.overview : {};
                const nameParts = String(existing.contact?.name || '')
                    .trim()
                    .split(/\s+/)
                    .filter(Boolean);

                // Prefer meta (form shape), fall back to root contact/title/lineItems
                // (convert-from-deficiency / generate-from-inspection historically only wrote roots).
                const loadedClient: ClientInfo = {
                    firstName: clientMeta.firstName || nameParts[0] || '',
                    lastName: clientMeta.lastName || nameParts.slice(1).join(' ') || '',
                    buildingName: clientMeta.buildingName || '',
                    address: clientMeta.address || existing.contact?.address || '',
                    phone: clientMeta.phone || existing.contact?.phone || '',
                    email: clientMeta.email || existing.contact?.email || '',
                    corpNo: clientMeta.corpNo || '',
                    representativeName: clientMeta.representativeName || ''
                };
                setClient(loadedClient);
                setConfirmEmail(loadedClient.email || '');
                setOverview({
                    jobTitle: overviewMeta.jobTitle || existing.title || '',
                    salesperson: overviewMeta.salesperson || 'Fermin Fierro',
                    introduction: overviewMeta.introduction || existing.notes || ''
                });

                const metaLineItems = Array.isArray(meta.lineItems) ? meta.lineItems : [];
                const rootLineItems = Array.isArray(existing.lineItems) ? existing.lineItems : [];
                setLineItems(ensureLineItemIds(metaLineItems.length > 0 ? metaLineItems : rootLineItems));

                if (meta.pricing) {
                    setDiscount(meta.pricing.discount || 0);
                    setTaxRate(meta.pricing.taxRate || 13);
                    setTaxName(meta.pricing.taxName || 'HST');
                } else {
                    setDiscount(existing.discountPercent || 0);
                    setTaxRate(typeof existing.taxPercent === 'number' ? existing.taxPercent : 13);
                }
                if (meta.attachments !== undefined) setAttachments(meta.attachments);
                if (meta.imageDescription !== undefined) setImageDescription(meta.imageDescription);
                if (meta.customerReview !== undefined) setCustomerReview(meta.customerReview);
                if (meta.clientMessage !== undefined) setClientMessage(meta.clientMessage);
                if (meta.customSections && typeof meta.customSections === 'object') {
                    setCustomSectionValues((prev) => {
                        const next = { ...prev };
                        for (const [id, raw] of Object.entries(
                            meta.customSections as Record<string, { title?: string; text?: string }>
                        )) {
                            next[id] = {
                                title: (raw?.title || next[id]?.title || 'Section').trim() || 'Section',
                                text: raw?.text ?? next[id]?.text ?? ''
                            };
                        }
                        return next;
                    });
                }
            })
            .catch(() => {
                setCurrentQuoteId(null);
                setErrorMessage('Failed to load existing quote. You can continue creating a new one.');
            });
    }, [searchParams]);

    const visibleCustomSections = useMemo(() => studioCustomSections, [studioCustomSections]);

    useEffect(() => {
        if (!showClientPicker) return;

        const query = clientSearch.trim();
        const timer = window.setTimeout(() => {
            setIsClientSearchLoading(true);
            apiRequest<{ data: ExistingClient[] }>(`/api/clients?q=${encodeURIComponent(query)}&limit=50`)
                .then((res) => setExistingClients(res.data || []))
                .catch(() => setExistingClients([]))
                .finally(() => setIsClientSearchLoading(false));
        }, 250);

        return () => window.clearTimeout(timer);
    }, [showClientPicker, clientSearch]);

    // Handlers
    const handleAddLineItem = () => {
        setLineItems([
            ...lineItems,
            { id: Date.now().toString(), item: '', description: '', quantity: 1, unitPrice: 0 }
        ]);
    };

    const handleRemoveLineItem = (id: string) => {
        setLineItems(lineItems.filter(item => item.id !== id));
    };

    const handleLineItemChange = (id: string, field: keyof LineItem, value: any) => {
        setLineItems(lineItems.map(item => {
            if (item.id === id) {
                return { ...item, [field]: value };
            }
            return item;
        }));
    };

    const handleSelectExistingClient = (selected: ExistingClient) => {
        setClient({
            firstName: selected.firstName || '',
            lastName: selected.lastName || '',
            buildingName: selected.buildingName || '',
            address: selected.address || '',
            phone: selected.phone || '',
            email: selected.email || '',
            corpNo: selected.corpNo || '',
            representativeName: selected.representativeName || ''
        });
        setShowClientPicker(false);
        setClientSearch('');
    };

    const validateForPdf = (): string[] => {
        const missing: string[] = [];

        if (!client.firstName.trim()) missing.push('Client first name');
        if (!client.lastName.trim()) missing.push('Client last name');
        if (!client.email.trim()) missing.push('Client email');
        if (client.email.trim() && !isValidEmail(client.email)) missing.push('Client email must be valid');
        if (!client.phone.trim()) missing.push('Client phone number');
        if (client.phone.trim() && !isValidPhoneNumber(client.phone)) {
            missing.push('Client phone must be 10 digits (or 11 digits starting with 1, e.g. +1)');
        }
        if (!overview.jobTitle.trim()) missing.push('Job title');
        if (!overview.salesperson.trim()) missing.push('Salesperson');
        if (lineItems.length === 0) missing.push('At least one line item');

        const hasInvalidLineItem = lineItems.some(
            (item) => !item.item.trim() || item.quantity <= 0 || item.unitPrice < 0
        );
        if (hasInvalidLineItem) {
            missing.push('Line items must include item name, quantity > 0, and valid unit price');
        }

        return missing;
    };

    const canGeneratePdf = (): boolean => {
        const missing = validateForPdf();
        if (missing.length > 0) {
            setErrorMessage(`Please complete required fields before generating PDF:\n- ${missing.join('\n- ')}`);
            return false;
        }
        return true;
    };

    const handleReviewClick = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        try {
            setIsSaving(true);
            await saveQuote('Draft');
            setConfirmEmail(client.email);
            setShowPreview(true);
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to prepare quote preview.';
            setErrorMessage(message);
        } finally {
            setIsSaving(false);
        }
    };

    const saveQuote = async (
        status: 'Draft' | 'Sent',
        email?: string
    ): Promise<{ id: string; serialNumber: number | null }> => {
        const payload = {
            title: overview.jobTitle || 'Untitled Quote',
            status: status === 'Sent' ? 'sent' : 'draft',
            contact: {
                name: `${client.firstName} ${client.lastName}`.trim() || 'New Client',
                email: email || client.email,
                phone: client.phone,
                address: client.address
            },
            lineItems,
            notes: overview.introduction,
            subtotal,
            discountPercent: discount,
            taxPercent: taxRate,
            total,
            serialNumber: currentQuoteSerial,
            meta: {
                client,
                overview,
                lineItems,
                pricing: {
                    discount,
                    taxRate,
                    taxName
                },
                attachments,
                imageDescription,
                customerReview,
                clientMessage,
                disclaimer,
                customSections: Object.fromEntries(
                    visibleCustomSections.map((sec) => [
                        sec.id,
                        {
                            title: sec.title || customSectionValues[sec.id]?.title || 'Section',
                            text: customSectionValues[sec.id]?.text || ''
                        }
                    ])
                )
            }
        };

        if (currentQuoteId) {
            const updated = await apiRequest<{ data: any }>(`/api/quotes/${currentQuoteId}`, {
                method: 'PATCH',
                json: payload
            });
            setCurrentQuoteId(updated.data._id);
            const serialNumber = typeof updated.data.serialNumber === 'number' ? updated.data.serialNumber : null;
            setCurrentQuoteSerial(serialNumber);
            return { id: updated.data._id, serialNumber };
        } else {
            const created = await apiRequest<{ data: any }>('/api/quotes', {
                method: 'POST',
                json: payload
            });
            setCurrentQuoteId(created.data._id);
            const serialNumber = typeof created.data.serialNumber === 'number' ? created.data.serialNumber : null;
            setCurrentQuoteSerial(serialNumber);
            return { id: created.data._id, serialNumber };
        }
    };

    const handleSendEmail = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        if (!confirmEmail || !isValidEmail(confirmEmail)) {
            setErrorMessage('Please enter a valid email address.');
            return;
        }

        setIsSending(true);
        saveQuote('Sent', confirmEmail)
            .then(({ id }) =>
                apiRequest('/api/quotes/send', {
                    method: 'POST',
                    json: { quoteId: id, email: confirmEmail }
                })
            )
            .then(() => {
                setIsSending(false);
                setShowPreview(false);
                setQuoteStatus('sent');
                setSuccessMessage(
                    quoteStatus !== 'draft'
                        ? `Quote successfully resent to ${confirmEmail}!`
                        : `Quote successfully sent to ${confirmEmail}!`
                );
                navigate('/admin/quotes');
            })
            .catch((error: unknown) => {
                setIsSending(false);
                const message = error instanceof Error ? error.message : 'Failed to send quote.';
                setErrorMessage(message);
            });
    };

    const handleSave = () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        setIsSaving(true);
        saveQuote('Draft')
            .then(() => {
                setSuccessMessage('Quote saved as draft.');
                navigate('/admin/quotes');
            })
            .catch((error: unknown) => {
                const message = error instanceof Error ? error.message : 'Failed to save quote.';
                setErrorMessage(message);
            })
            .finally(() => {
                setIsSaving(false);
            });
    };

    const handleDownloadPdf = async () => {
        setErrorMessage(null);
        setSuccessMessage(null);
        if (!canGeneratePdf()) return;
        try {
            setIsDownloading(true);
            const { id } = await saveQuote('Draft');
            const brandingRes = await apiRequest<{
                branding?: { pdfVariant?: string; useClientRasterExport?: boolean } | null;
            }>('/api/branding').catch(() => ({ branding: null }));
            const useZeraDesign = brandingRes.branding?.useClientRasterExport === true;

            if (useZeraDesign) {
                const openedForDownload = !showPreview;
                if (openedForDownload) {
                    setConfirmEmail(client.email);
                    setIsPreparingPdf(true);
                    setShowPreview(true);
                    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
                    await new Promise((resolve) => requestAnimationFrame(() => resolve(null)));
                }

                const previewElement = document.querySelector('.modal-content .pdf-page') as HTMLElement | null;
                if (!previewElement) {
                    throw new Error('Preview element not found.');
                }
                await exportElementToPdf(previewElement, overview.jobTitle || 'Quote');
                if (openedForDownload) {
                    setShowPreview(false);
                    setIsPreparingPdf(false);
                }
            } else {
                const fileNameBase = (overview.jobTitle || 'Quote').trim() || 'Quote';
                await downloadApiFile(`/api/quotes/pdf?quoteId=${encodeURIComponent(id)}`, `${fileNameBase}.pdf`);
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to download quote PDF.';
            setErrorMessage(message);
        } finally {
            setIsDownloading(false);
        }
    };

    // Format helpers
    const fmtCurrency = (val: number) => `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    return (
        <AppShell>
            <div className="quote-page">

                {errorMessage && (
                    <div className="banner banner-error">
                        {errorMessage.split('\n').map((line) => (
                            <div key={line}>{line}</div>
                        ))}
                    </div>
                )}
                {successMessage && (
                    <div className="banner banner-success">
                        {successMessage}
                    </div>
                )}

                {/* Review & Send Modal */}
                {showPreview && (
                    <div className={`modal-overlay${isPreparingPdf ? ' modal-overlay-hidden' : ''}`}>
                        <div className="modal-content">
                            <div className="modal-header">
                                <h2>Review and Send Quote</h2>
                                <button className="btn-icon" onClick={() => setShowPreview(false)}>✕</button>
                            </div>
                            <div className="modal-body">

                                {/* Left Panel: Email Controls */}
                                <div className="email-confirm-panel">
                                    <h3>Recipient Details</h3>
                                    <p className="panel-info">
                                        Please confirm the client's email address below. A PDF copy of this quote will be attached automatically.
                                    </p>
                                    <div className="form-group">
                                        <label className="form-label">Client Email</label>
                                        <input
                                            className="form-input"
                                            type="email"
                                            value={confirmEmail}
                                            onChange={e => setConfirmEmail(e.target.value)}
                                            placeholder="client@example.com"
                                            autoFocus
                                        />
                                    </div>
                                    <div className="form-group" style={{ marginTop: '24px' }}>
                                        <label className="form-label">Subject Line</label>
                                        <input
                                            className="form-input"
                                            disabled
                                            value={`Estimate for ${overview.jobTitle} - Zera Fire Protection`}
                                        />
                                    </div>
                                </div>

                                {/* Right Panel: PDF Preview */}
                                <div className="pdf-preview-container">
                                    <div className="pdf-page" data-pdf-exporting="true">
                                        <div className="pdf-brand-header" data-pdf-header>
                                            <div className="pdf-brand-left">
                                                <img className="pdf-brand-logo-img" src={zeraStylePdfLogoSrc} alt="Company logo" />
                                            </div>
                                            <div className="pdf-brand-right">
                                                <img className="pdf-brand-member-cfaa" src="/assets/branding/cfaa.png" alt="CFAA member" />
                                                <img className="pdf-brand-member-acmo" src="/assets/branding/acmo.png" alt="ACMO member" />
                                            </div>
                                        </div>
                                        <div className="pdf-body-content">
                                            <div className="invoice-header">
                                                <div>
                                                    <div className="invoice-title">QUOTE</div>
                                                    <div style={{ marginTop: '10px', color: '#666' }}>
                                                        Quote #{currentQuoteSerial ?? '—'}
                                                    </div>
                                                </div>
                                                <div className="company-details">
                                                    <strong>{COMPANY_NAME}</strong><br />
                                                    {COMPANY_ADDRESS}<br />
                                                    {COMPANY_PHONE}<br />
                                                    {COMPANY_EMAIL}<br />
                                                    {COMPANY_WEBSITE}
                                                </div>
                                            </div>

                                            <div className="invoice-meta-grid">
                                                <div className="meta-group">
                                                    <h3>Bill To</h3>
                                                    <div className="meta-content">
                                                        {client.firstName} {client.lastName}<br />
                                                        {client.address || 'No Address Provided'}<br />
                                                        {client.email}<br />
                                                        {client.phone}
                                                        {client.corpNo && <><br />Corp No.: {client.corpNo}</>}
                                                        {client.representativeName && <><br />Representative: {client.representativeName}</>}
                                                    </div>
                                                </div>
                                                <div className="meta-group">
                                                    <h3>Job Details</h3>
                                                    <div className="meta-content">
                                                        <strong>{overview.jobTitle}</strong><br />
                                                        Salesperson: {overview.salesperson}<br />
                                                        Date: {new Date().toLocaleDateString()}<br />
                                                        Valid Until: {new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toLocaleDateString()}
                                                    </div>
                                                </div>
                                            </div>

                                            <table className="invoice-table">
                                                <thead>
                                                    <tr>
                                                        <th>Item</th>
                                                        <th>Qty</th>
                                                        <th>Rate</th>
                                                        <th>Amount</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {lineItems.map(item => (
                                                        <tr key={item.id}>
                                                            <td>
                                                                <strong>{item.item}</strong>
                                                                <div
                                                                    style={{
                                                                        fontSize: '0.85rem',
                                                                        color: '#888',
                                                                        whiteSpace: 'pre-wrap',
                                                                        lineHeight: 1.45
                                                                    }}
                                                                >
                                                                    {item.description}
                                                                </div>
                                                            </td>
                                                            <td>{item.quantity}</td>
                                                            <td>{fmtCurrency(item.unitPrice)}</td>
                                                            <td>{fmtCurrency(item.quantity * item.unitPrice)}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>

                                            <div className="invoice-totals">
                                                <div className="total-row">
                                                    <span>Subtotal</span>
                                                    <span>{fmtCurrency(subtotal)}</span>
                                                </div>
                                                {discount > 0 && (
                                                    <div className="total-row" style={{ color: '#d4492c' }}>
                                                        <span>Discount ({discount}%)</span>
                                                        <span>-{fmtCurrency(discountAmount)}</span>
                                                    </div>
                                                )}
                                                <div className="total-row">
                                                    <span>{taxName} ({taxRate}%)</span>
                                                    <span>{fmtCurrency(taxAmount)}</span>
                                                </div>
                                                <div className="total-row final">
                                                    <span>Total</span>
                                                    <span>{fmtCurrency(total)}</span>
                                                </div>
                                                <div className="total-row" style={{ color: '#2d8dcc', fontWeight: 600, marginTop: '8px' }}>
                                                    <span>Required Deposit</span>
                                                    <span>{fmtCurrency(deposit)}</span>
                                                </div>
                                            </div>

                                            <div
                                                className="pdf-intro-section pdf-keep-together"
                                                data-pdf-keep-together
                                                style={{ marginTop: '40px' }}
                                            >
                                                {studioIntroTitle ? <h3>{studioIntroTitle}</h3> : null}
                                                <p
                                                    style={{
                                                        fontSize: '0.9rem',
                                                        color: '#555',
                                                        lineHeight: '1.6',
                                                        whiteSpace: 'pre-wrap'
                                                    }}
                                                >
                                                    {overview.introduction || 'Thank you for your business.'}
                                                </p>
                                            </div>

                                            {visibleCustomSections.map((sec) => {
                                                const values = customSectionValues[sec.id];
                                                if (!values?.text?.trim() && !values?.title?.trim()) return null;
                                                return (
                                                    <div
                                                        key={sec.id}
                                                        className="pdf-custom-body-section pdf-keep-together"
                                                        data-pdf-keep-together
                                                        style={{ marginTop: '24px' }}
                                                    >
                                                        <h3>{sec.title || values?.title || 'Section'}</h3>
                                                        <p
                                                            style={{
                                                                fontSize: '0.9rem',
                                                                color: '#555',
                                                                lineHeight: '1.6',
                                                                whiteSpace: 'pre-wrap'
                                                            }}
                                                        >
                                                            {values?.text || ''}
                                                        </p>
                                                    </div>
                                                );
                                            })}

                                            {studioSignatureEnabled ? (
                                                <div
                                                    className="pdf-signature-section pdf-keep-together"
                                                    data-pdf-keep-together
                                                    style={{ marginTop: 22, maxWidth: 520 }}
                                                >
                                                    <h3
                                                        style={{
                                                            margin: '0 0 10px',
                                                            fontSize: 12,
                                                            fontWeight: 600,
                                                            color: '#475569'
                                                        }}
                                                    >
                                                        {studioSignatureTitle}
                                                    </h3>
                                                    <table
                                                        style={{
                                                            width: '100%',
                                                            borderCollapse: 'collapse',
                                                            tableLayout: 'fixed'
                                                        }}
                                                    >
                                                        <tbody>
                                                            <tr>
                                                                <td
                                                                    style={{
                                                                        width: '58%',
                                                                        padding: '0 20px 0 0',
                                                                        verticalAlign: 'bottom'
                                                                    }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            borderBottom: '1px solid #94a3b8',
                                                                            height: 18,
                                                                            maxWidth: 260
                                                                        }}
                                                                    />
                                                                    <div
                                                                        style={{
                                                                            marginTop: 4,
                                                                            fontSize: 10,
                                                                            letterSpacing: '0.04em',
                                                                            textTransform: 'uppercase',
                                                                            color: '#94a3b8'
                                                                        }}
                                                                    >
                                                                        Signature
                                                                    </div>
                                                                </td>
                                                                <td style={{ width: '42%', padding: 0, verticalAlign: 'bottom' }}>
                                                                    <div
                                                                        style={{
                                                                            borderBottom: '1px solid #94a3b8',
                                                                            height: 18,
                                                                            maxWidth: 140
                                                                        }}
                                                                    />
                                                                    <div
                                                                        style={{
                                                                            marginTop: 4,
                                                                            fontSize: 10,
                                                                            letterSpacing: '0.04em',
                                                                            textTransform: 'uppercase',
                                                                            color: '#94a3b8'
                                                                        }}
                                                                    >
                                                                        Date
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <td
                                                                    colSpan={2}
                                                                    style={{ padding: '14px 0 0', verticalAlign: 'bottom' }}
                                                                >
                                                                    <div
                                                                        style={{
                                                                            borderBottom: '1px solid #94a3b8',
                                                                            height: 18,
                                                                            maxWidth: 260
                                                                        }}
                                                                    />
                                                                    <div
                                                                        style={{
                                                                            marginTop: 4,
                                                                            fontSize: 10,
                                                                            letterSpacing: '0.04em',
                                                                            textTransform: 'uppercase',
                                                                            color: '#94a3b8'
                                                                        }}
                                                                    >
                                                                        Print name
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            ) : null}

                                            <div className="pdf-fixed-footer" data-pdf-footer>
                                                <div className="invoice-footer-note">
                                                    {disclaimer}
                                                    <br />
                                                    {COMPANY_NAME} • {COMPANY_WEBSITE}
                                                </div>

                                                <div className="invoice-compliance-footer">
                                                    Services performed in accordance with CAN/ULC Standards and Ontario Fire Code.
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                            </div>
                            <div className="modal-footer">
                                <button className="btn-secondary" onClick={() => setShowPreview(false)}>Cancel</button>
                                <button className="btn-secondary" onClick={handleDownloadPdf} disabled={isDownloading || isSending}>
                                    {isDownloading ? 'Downloading...' : 'Download PDF'}
                                </button>
                                <button className="btn-primary" onClick={handleSendEmail} disabled={isSending}>
                                    {isSending
                                        ? quoteStatus !== 'draft'
                                            ? 'Resending...'
                                            : 'Sending...'
                                        : quoteStatus !== 'draft'
                                          ? 'Resend Quote & Email'
                                          : 'Send Quote & Email'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                <div className="quote-container">
                    <header className="quote-header">
                        <div>
                            <h1>{currentQuoteId ? 'Edit Quote' : 'New Quote'}</h1>
                            <span style={{ color: 'var(--dash-muted)' }}>
                                {currentQuoteId
                                    ? `Editing Quote #${currentQuoteSerial ?? '—'}`
                                    : 'Generate a professional service quote'}
                            </span>
                        </div>
                        <button className="btn-icon" onClick={() => navigate(-1)} style={{ fontSize: '1.5rem' }}>✕</button>
                    </header>

                    {/* 1. Client Information */}
                    <section className="quote-card">
                        <div className="quote-section-title">Client Information</div>

                        <div className="form-group">
                            <button
                                type="button"
                                className="btn-add-item"
                                style={{ justifyContent: 'center', marginBottom: '16px' }}
                                onClick={() => setShowClientPicker((prev) => !prev)}
                            >
                                🔍 {showClientPicker ? 'Hide Existing Clients' : 'Select Existing Client'}
                            </button>
                            {showClientPicker && (
                                <div style={{ border: '1px solid var(--dash-border)', borderRadius: '12px', padding: '12px', marginBottom: '16px', background: '#fff' }}>
                                    <input
                                        className="form-input"
                                        placeholder="Search by building name or address"
                                        value={clientSearch}
                                        onChange={(e) => setClientSearch(e.target.value)}
                                    />
                                    <div style={{ marginTop: '10px', maxHeight: '220px', overflowY: 'auto', display: 'grid', gap: '8px' }}>
                                        {isClientSearchLoading && <div style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}>Searching clients...</div>}
                                        {!isClientSearchLoading && existingClients.length === 0 && (
                                            <div style={{ color: 'var(--dash-muted)', fontSize: '0.9rem' }}>No matching clients found.</div>
                                        )}
                                        {!isClientSearchLoading && existingClients.map((c) => (
                                            <button
                                                key={c._id}
                                                type="button"
                                                className="dash-btn dash-btn-sm"
                                                style={{ textAlign: 'left', justifyContent: 'flex-start' }}
                                                onClick={() => handleSelectExistingClient(c)}
                                            >
                                                {(c.firstName || c.lastName) ? `${c.firstName || ''} ${c.lastName || ''}`.trim() : 'Client'}
                                                {' • '}
                                                {c.buildingName || c.address || 'No address'}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">First Name</label>
                                <input
                                    className="form-input"
                                    value={client.firstName}
                                    onChange={e => setClient({ ...client, firstName: e.target.value })}
                                    placeholder="Jane"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Last Name</label>
                                <input
                                    className="form-input"
                                    value={client.lastName}
                                    onChange={e => setClient({ ...client, lastName: e.target.value })}
                                    placeholder="Doe"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label">Building Name</label>
                                <input
                                    className="form-input"
                                    value={client.buildingName}
                                    onChange={e => setClient({ ...client, buildingName: e.target.value })}
                                    placeholder="e.g. Maple Heights Tower"
                                />
                            </div>
                            <div className="form-group full-width">
                                <label className="form-label">Property Address</label>
                                <input
                                    className="form-input"
                                    value={client.address}
                                    onChange={e => setClient({ ...client, address: e.target.value })}
                                    placeholder="123 Fire Lane, Safety City, ST"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Phone</label>
                                <input
                                    className="form-input"
                                    value={client.phone}
                                    onChange={e => setClient({ ...client, phone: e.target.value })}
                                    placeholder="(555) 123-4567"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Email</label>
                                <input
                                    className="form-input"
                                    value={client.email}
                                    onChange={e => setClient({ ...client, email: e.target.value })}
                                    placeholder="jane@example.com"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Corp No.</label>
                                <input
                                    className="form-input"
                                    value={client.corpNo}
                                    onChange={e => setClient({ ...client, corpNo: e.target.value })}
                                    placeholder="123456789"
                                />
                            </div>
                            <div className="form-group">
                                <label className="form-label">Representative Name (Company)</label>
                                <input
                                    className="form-input"
                                    value={client.representativeName}
                                    onChange={e => setClient({ ...client, representativeName: e.target.value })}
                                    placeholder="John Smith"
                                />
                            </div>
                        </div>
                    </section>

                    {/* 2. Overview */}
                    <section className="quote-card">
                        <div className="quote-section-title">Overview</div>
                        <div className="form-grid">
                            <div className="form-group">
                                <label className="form-label">Quote #</label>
                                <input
                                    type="number"
                                    className="form-input"
                                    placeholder="Auto-generated"
                                    value={currentQuoteSerial ?? ''}
                                    onChange={e => {
                                        const val = e.target.value;
                                        setCurrentQuoteSerial(val === '' ? null : parseInt(val, 10) || null);
                                    }}
                                />
                                {!currentQuoteId && (
                                    <span style={{ fontSize: '0.8rem', color: 'var(--dash-muted)', fontStyle: 'italic', marginTop: '2px' }}>
                                        Will be auto-assigned on first save if left empty
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className="form-group" style={{ marginTop: '16px' }}>
                            <label className="form-label">Job Title</label>
                            <input
                                className="form-input"
                                value={overview.jobTitle}
                                onChange={e => setOverview({ ...overview, jobTitle: e.target.value })}
                                placeholder="e.g. Annual Fire Inspection"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Salesperson</label>
                            <input
                                className="form-input"
                                value={overview.salesperson}
                                onChange={e => setOverview({ ...overview, salesperson: e.target.value })}
                                placeholder="Enter salesperson name"
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">{studioIntroTitle || 'Introduction'}</label>
                            <textarea
                                className="form-textarea"
                                value={overview.introduction}
                                onChange={e => setOverview({ ...overview, introduction: e.target.value })}
                                placeholder="Scope and intent..."
                            />
                        </div>
                    </section>

                    {visibleCustomSections.length > 0 ? (
                        <section className="quote-card">
                            <div className="quote-section-title">PDF body sections</div>
                            <p style={{ margin: '0 0 14px', color: 'var(--dash-muted)', fontSize: '0.9rem', lineHeight: 1.45 }}>
                                These fields come from PDF Studio → Body & Table → Sections. Content is saved with
                                this quote and printed in the PDF where each section is placed.
                            </p>
                            {visibleCustomSections.map((sec) => {
                                const values = customSectionValues[sec.id] || {
                                    title: sec.title || 'Section',
                                    text: ''
                                };
                                return (
                                    <div key={sec.id} className="form-group">
                                        <label className="form-label">{sec.title || values.title || 'Section'}</label>
                                        <textarea
                                            className="form-textarea"
                                            value={values.text}
                                            onChange={(e) =>
                                                setCustomSectionValues((prev) => ({
                                                    ...prev,
                                                    [sec.id]: {
                                                        title: sec.title || values.title || 'Section',
                                                        text: e.target.value
                                                    }
                                                }))
                                            }
                                            placeholder={`Enter content for “${sec.title || values.title || 'this section'}”…`}
                                            rows={4}
                                        />
                                    </div>
                                );
                            })}
                        </section>
                    ) : null}

                    {/* 3. Line Items */}
                    <section className="quote-card">
                        <div className="quote-section-title">Line Items</div>

                        <div className="line-items-header">
                            <span className="form-label">Item / Description</span>
                            <span className="form-label">Qty</span>
                            <span className="form-label">Unit Price</span>
                            <span className="form-label">Total</span>
                            <span></span>
                        </div>

                        {lineItems.map(item => (
                            <div key={item.id} className="line-item-row">
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                    <input
                                        className="form-input"
                                        placeholder="Item Name"
                                        value={item.item}
                                        onChange={e => handleLineItemChange(item.id, 'item', e.target.value)}
                                    />
                                    <textarea
                                        className="form-textarea line-item-description"
                                        placeholder="Description — press Enter for a new line"
                                        rows={2}
                                        value={item.description}
                                        onChange={e => handleLineItemChange(item.id, 'description', e.target.value)}
                                    />
                                </div>
                                <div>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder="0"
                                        value={displayNumberInput(item.quantity)}
                                        onChange={e => handleLineItemChange(item.id, 'quantity', e.target.value === '' ? 0 : Number(e.target.value))}
                                    />
                                </div>
                                <div>
                                    <input
                                        type="number"
                                        className="form-input"
                                        placeholder="0"
                                        value={displayNumberInput(item.unitPrice)}
                                        onChange={e => handleLineItemChange(item.id, 'unitPrice', e.target.value === '' ? 0 : Number(e.target.value))}
                                    />
                                </div>
                                <div style={{ paddingTop: '12px', fontWeight: 'bold' }}>
                                    ${(item.quantity * item.unitPrice).toFixed(2)}
                                </div>
                                <div className="line-item-actions">
                                    <button className="btn-icon" onClick={() => handleRemoveLineItem(item.id)}>🗑️</button>
                                </div>
                            </div>
                        ))}

                        <button className="btn-add-item" onClick={handleAddLineItem}>
                            <span>+ Add Line Item</span>
                        </button>

                        {/* 4. Pricing Summary */}
                        <div className="pricing-summary">
                            <div className="pricing-row">
                                <span>Subtotal</span>
                                <span>${subtotal.toFixed(2)}</span>
                            </div>
                            <div className="pricing-row">
                                <span>Discount (%)</span>
                                <input
                                    type="number"
                                    className="form-input"
                                    style={{ width: '80px', padding: '4px', textAlign: 'right' }}
                                    placeholder="0"
                                    value={displayNumberInput(discount)}
                                    onChange={e => setDiscount(e.target.value === '' ? 0 : Number(e.target.value))}
                                />
                            </div>
                            <div className="pricing-row">
                                <input
                                    className="form-input"
                                    style={{ width: '80px', padding: '4px', textAlign: 'right', border: 'none', background: 'transparent', textAlignLast: 'end' }}
                                    value={taxName}
                                    onChange={e => setTaxName(e.target.value)}
                                />
                                <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                    <input
                                        type="number"
                                        className="form-input"
                                        style={{ width: '60px', padding: '4px', textAlign: 'right' }}
                                        placeholder="0"
                                        value={displayNumberInput(taxRate)}
                                        onChange={e => setTaxRate(e.target.value === '' ? 0 : Number(e.target.value))}
                                    />
                                    <span>%</span>
                                </div>
                            </div>
                            <div className="pricing-row total">
                                <span>Total</span>
                                <span>${total.toFixed(2)}</span>
                            </div>
                            <div className="pricing-row deposit">
                                <span>Required Deposit (30%)</span>
                                <span>${deposit.toFixed(2)}</span>
                            </div>
                        </div>
                    </section>

                    {/* 5. Additional Info */}
                    <section className="quote-card">
                        <div className="quote-section-title">Attachments & Details</div>
                        <div className="form-group">
                            <label className="form-label">Attachments (not actual files)</label>
                            <input
                                className="form-input"
                                placeholder="Checklist.pdf, Floorplan.jpg"
                                value={attachments}
                                onChange={e => setAttachments(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Image Description</label>
                            <input
                                className="form-input"
                                placeholder="e.g. Photo of panel in lobby"
                                value={imageDescription}
                                onChange={e => setImageDescription(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Customer Review Quote</label>
                            <textarea
                                className="form-textarea"
                                placeholder='"Great service!" - Client'
                                value={customerReview}
                                onChange={e => setCustomerReview(e.target.value)}
                                style={{ minHeight: '60px' }}
                            />
                        </div>
                        <div className="form-group">
                            <label className="form-label">Client Message</label>
                            <textarea
                                className="form-textarea"
                                value={clientMessage}
                                onChange={e => setClientMessage(e.target.value)}
                            />
                        </div>
                        <div className="form-group">
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <label className="form-label">Contract / Disclaimer</label>
                                <span style={{ fontSize: '0.8rem', color: 'green' }}>✓ Standard Text Loaded</span>
                            </div>
                            <p style={{ fontSize: '0.85rem', color: 'var(--dash-muted)', fontStyle: 'italic', marginTop: '4px' }}>
                                {disclaimer}
                            </p>
                        </div>
                    </section>

                    {/* Actions */}
                    <div className="quote-actions">
                        <button className="btn-secondary" onClick={handleSave} disabled={isSaving || isSending || isDownloading}>
                            {isSaving ? 'Saving...' : 'Save Draft'}
                        </button>
                        <button className="btn-secondary" onClick={handleDownloadPdf} disabled={isDownloading || isSaving || isSending}>
                            {isDownloading ? 'Downloading...' : 'Download PDF'}
                        </button>
                        <button className="btn-primary" onClick={handleReviewClick} disabled={isSaving || isSending || isDownloading}>
                            {isSaving ? 'Preparing...' : 'Review and Send'}
                        </button>
                    </div>

                </div>
            </div>
        </AppShell>
    );
}
